import { useCallback, useMemo, useState } from 'react';
import type { Module } from './mock';
import type { TeamType } from './teams';

/**
 * Per-discipline catalogs of the common problems each team faces. Selecting one
 * pre-fills the ticket (module, priority, category, subject). Teams can also add
 * their own (custom problems persist in localStorage now → a per-tenant
 * `problem_catalog` table, RLS-scoped, in production).
 */
export type ProblemDef = {
  id: string;
  label: string;
  module: Module;
  priority: 'P1' | 'P2' | 'P3' | 'P4';
  category: string;
  custom?: boolean;
};

const CATALOG: Record<TeamType, ProblemDef[]> = {
  it: [
    { id: 'it-boot', label: 'Laptop won’t boot / hardware failure', module: 'Incident', priority: 'P2', category: 'Hardware' },
    { id: 'it-vpn', label: 'VPN won’t connect / keeps dropping', module: 'Incident', priority: 'P2', category: 'Network' },
    { id: 'it-pwd', label: 'Password reset / account locked out', module: 'Incident', priority: 'P3', category: 'Access' },
    { id: 'it-mfa', label: 'Lost MFA device / can’t authenticate', module: 'Incident', priority: 'P2', category: 'Access' },
    { id: 'it-email', label: 'Email not syncing / can’t send', module: 'Incident', priority: 'P3', category: 'Software' },
    { id: 'it-wifi', label: 'Wi-Fi / connectivity issue', module: 'Incident', priority: 'P3', category: 'Network' },
    { id: 'it-printer', label: 'Printer offline / won’t print', module: 'Incident', priority: 'P4', category: 'Hardware' },
    { id: 'it-slow', label: 'Computer slow / freezing', module: 'Incident', priority: 'P3', category: 'Hardware' },
    { id: 'it-app', label: 'Application crashing / errors', module: 'Incident', priority: 'P3', category: 'Software' },
    { id: 'it-drive', label: 'Shared drive / file access denied', module: 'Incident', priority: 'P3', category: 'Access' },
    { id: 'it-sw', label: 'Software install / license request', module: 'Service Request', priority: 'P4', category: 'Software' },
    { id: 'it-hw', label: 'New hardware / peripheral request', module: 'Service Request', priority: 'P4', category: 'Hardware' },
    { id: 'it-access', label: 'Access / permissions request', module: 'Service Request', priority: 'P3', category: 'Access' },
    { id: 'it-onboard', label: 'New-hire IT onboarding', module: 'Service Request', priority: 'P3', category: 'Onboarding' },
  ],
  soc: [
    { id: 'soc-phish', label: 'Phishing email reported', module: 'Incident', priority: 'P2', category: 'Phishing' },
    { id: 'soc-malware', label: 'Malware / ransomware detected', module: 'Incident', priority: 'P1', category: 'Malware' },
    { id: 'soc-login', label: 'Suspicious login / impossible travel', module: 'Incident', priority: 'P1', category: 'Identity' },
    { id: 'soc-compromise', label: 'Account compromise suspected', module: 'Incident', priority: 'P1', category: 'Identity' },
    { id: 'soc-exfil', label: 'Data exfiltration suspected', module: 'Incident', priority: 'P1', category: 'Data Loss' },
    { id: 'soc-c2', label: 'C2 / beacon traffic detected', module: 'Incident', priority: 'P1', category: 'Network' },
    { id: 'soc-brute', label: 'Brute-force / credential stuffing', module: 'Incident', priority: 'P2', category: 'Identity' },
    { id: 'soc-priv', label: 'Privilege escalation detected', module: 'Incident', priority: 'P1', category: 'Identity' },
    { id: 'soc-vuln', label: 'Critical vulnerability / CVE', module: 'Problem', priority: 'P2', category: 'Vulnerability' },
    { id: 'soc-ddos', label: 'DDoS / availability attack', module: 'Incident', priority: 'P1', category: 'Network' },
    { id: 'soc-insider', label: 'Insider threat / policy violation', module: 'Incident', priority: 'P2', category: 'Insider' },
    { id: 'soc-ioc', label: 'Suspicious file / IOC review', module: 'Incident', priority: 'P3', category: 'Triage' },
  ],
  dev: [
    { id: 'dev-outage', label: 'Service outage / 5xx errors', module: 'Incident', priority: 'P1', category: 'Availability' },
    { id: 'dev-latency', label: 'High latency / performance degradation', module: 'Incident', priority: 'P2', category: 'Performance' },
    { id: 'dev-deploy', label: 'Deployment / release failure', module: 'Incident', priority: 'P2', category: 'Release' },
    { id: 'dev-db', label: 'Database issue / slow queries', module: 'Incident', priority: 'P2', category: 'Database' },
    { id: 'dev-oom', label: 'Memory leak / OOM kills', module: 'Problem', priority: 'P2', category: 'Capacity' },
    { id: 'dev-disk', label: 'Disk full / capacity', module: 'Incident', priority: 'P2', category: 'Capacity' },
    { id: 'dev-cert', label: 'Certificate expiry', module: 'Change', priority: 'P2', category: 'Security' },
    { id: 'dev-ci', label: 'CI/CD pipeline failure', module: 'Incident', priority: 'P3', category: 'Tooling' },
    { id: 'dev-bug', label: 'Bug report', module: 'Incident', priority: 'P3', category: 'Bug' },
    { id: 'dev-feature', label: 'Feature request', module: 'Service Request', priority: 'P4', category: 'Feature' },
    { id: 'dev-rollback', label: 'Rollback needed', module: 'Change', priority: 'P1', category: 'Release' },
  ],
  design: [
    { id: 'des-ui', label: 'New UI / UX design request', module: 'Service Request', priority: 'P3', category: 'UI/UX' },
    { id: 'des-brand', label: 'Brand asset request', module: 'Service Request', priority: 'P4', category: 'Brand' },
    { id: 'des-motion', label: 'Motion / animation request', module: 'Service Request', priority: 'P4', category: 'Motion' },
    { id: 'des-review', label: 'Design review / approval', module: 'Service Request', priority: 'P3', category: 'Review' },
    { id: 'des-figma', label: 'Figma handoff / spec', module: 'Service Request', priority: 'P3', category: 'Handoff' },
    { id: 'des-illus', label: 'Illustration request', module: 'Service Request', priority: 'P4', category: 'Illustration' },
    { id: 'des-collateral', label: 'Marketing collateral', module: 'Service Request', priority: 'P4', category: 'Marketing' },
    { id: 'des-system', label: 'Design system update', module: 'Change', priority: 'P3', category: 'System' },
  ],
  generic: [
    { id: 'gen-onboard', label: 'New-hire onboarding', module: 'Service Request', priority: 'P3', category: 'Onboarding' },
    { id: 'gen-offboard', label: 'Offboarding / departure', module: 'Service Request', priority: 'P3', category: 'Offboarding' },
    { id: 'gen-leave', label: 'Leave / time-off request', module: 'Service Request', priority: 'P4', category: 'Leave' },
    { id: 'gen-expense', label: 'Expense reimbursement', module: 'Service Request', priority: 'P4', category: 'Expense' },
    { id: 'gen-invoice', label: 'Invoice / payment processing', module: 'Service Request', priority: 'P3', category: 'Finance' },
    { id: 'gen-policy', label: 'Policy clarification', module: 'Service Request', priority: 'P4', category: 'Policy' },
    { id: 'gen-contract', label: 'Contract / document review', module: 'Service Request', priority: 'P3', category: 'Legal' },
    { id: 'gen-approval', label: 'Approval request', module: 'Service Request', priority: 'P3', category: 'Approval' },
    { id: 'gen-access', label: 'Access / equipment request', module: 'Service Request', priority: 'P4', category: 'Access' },
  ],
};

const LS_KEY = 'compass-custom-problems';

function loadCustom(): Record<string, ProblemDef[]> {
  try {
    return JSON.parse(localStorage.getItem(LS_KEY) || '{}');
  } catch {
    return {};
  }
}

function newId() {
  return typeof crypto !== 'undefined' && 'randomUUID' in crypto
    ? crypto.randomUUID()
    : `p-${Date.now()}`;
}

/** Catalog (predefined + the team's saved custom problems) with CRUD. */
export function useProblems(teamId: string, teamType: TeamType) {
  const [custom, setCustom] = useState<Record<string, ProblemDef[]>>(() => loadCustom());

  const persist = useCallback((next: Record<string, ProblemDef[]>) => {
    localStorage.setItem(LS_KEY, JSON.stringify(next));
    setCustom(next);
  }, []);

  const problems = useMemo(
    () => [...CATALOG[teamType], ...(custom[teamId] ?? [])],
    [teamType, teamId, custom],
  );

  const addProblem = useCallback(
    (p: Omit<ProblemDef, 'id' | 'custom'>) => {
      const all = loadCustom();
      const item: ProblemDef = { ...p, id: newId(), custom: true };
      const next = { ...all, [teamId]: [...(all[teamId] ?? []), item] };
      persist(next);
      return item;
    },
    [teamId, persist],
  );

  const removeProblem = useCallback(
    (id: string) => {
      const all = loadCustom();
      const next = { ...all, [teamId]: (all[teamId] ?? []).filter((p) => p.id !== id) };
      persist(next);
    },
    [teamId, persist],
  );

  return { problems, addProblem, removeProblem };
}
